import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { User, Camera, Loader2 } from "lucide-react";
import { formatToBRLDate, parseBRLDateToDB } from "@/utils/dateFormatter";
import { compressImage } from "@/lib/imageUtils";

interface ProfileCompletionGuardProps {
  children: React.ReactNode;
}

const ProfileCompletionGuard: React.FC<ProfileCompletionGuardProps> = ({ children }) => {
  const { user, profile, refreshProfile, loading: authLoading, isAdmin } = useAuth();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [gender, setGender] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!authLoading && user && profile) {
      if (isAdmin) {
        setIsVisible(false);
        return;
      }

      const hasValidAvatar = profile.avatar_url;
      const hasPhone = profile.phone && profile.phone.trim().length >= 10;
      // @ts-ignore
      const hasBirthDate = profile.birth_date;
      // @ts-ignore
      const hasGender = profile.gender;

      const isComplete = profile.full_name && 
        profile.full_name !== "Usuário" && 
        profile.full_name.trim().length > 2 &&
        hasPhone;
      
      setIsVisible(!isComplete);
      setName(profile.full_name || "");
      // @ts-ignore
      setPhone(profile.phone || "");
      // @ts-ignore
      setGender(profile.gender || "");
      setAvatarUrl(profile.avatar_url || null);
    }
  }, [authLoading, user, profile, isAdmin]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, "");
    if (val.length > 11) val = val.slice(0, 11);
    if (val.length > 2) val = `(${val.slice(0, 2)}) ${val.slice(2)}`;
    if (val.length > 10) val = `${val.slice(0, 10)}-${val.slice(10)}`;
    setPhone(val);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);
      if (!event.target.files || event.target.files.length === 0) return;

      const originalFile = event.target.files[0];
      // Compress the image before uploading to prevent slow uploads/freezing
      const file = await compressImage(originalFile, 800, 0.7);
      
      const fileExt = file.name.split(".").pop() || "jpg";
      const filePath = `${user?.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      let uploadRes = await supabase.storage
        .from("avatars")
        .upload(filePath, file, { upsert: true, cacheControl: "3600" });

      let isMediaBucket = false;
      if (uploadRes.error) {
        uploadRes = await supabase.storage
          .from("media")
          .upload(`avatars/${filePath}`, file, { upsert: true, cacheControl: "3600" });
        isMediaBucket = true;
      }

      if (uploadRes.error) throw uploadRes.error;

      const bucketName = isMediaBucket ? "media" : "avatars";
      const actualPath = isMediaBucket ? `avatars/${filePath}` : filePath;
      const { data } = supabase.storage
        .from(bucketName)
        .getPublicUrl(actualPath);

      setAvatarUrl(data.publicUrl);
    } catch (error: any) {
      console.error("Erro ao carregar foto:", error);
      toast.error("Erro ao carregar foto: " + (error.message || "Tente novamente"));
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || name.trim().length < 3) {
      toast.error("Por favor, insira o seu nome completo.");
      return;
    }
    const cleanPhone = phone.replace(/\D/g, "");
    if (cleanPhone.length < 10) {
      toast.error("Por favor, insira um WhatsApp válido com DDD.");
      return;
    }
    if (!birthDate || birthDate.length < 10) {
      toast.error("Por favor, preencha a data de nascimento.");
      return;
    }
    if (!gender) {
      toast.error("Por favor, selecione seu sexo.");
      return;
    }
    if (!avatarUrl) {
      // Optional now
    }

    try {
      setUpdating(true);
      const dbDate = parseBRLDateToDB(birthDate);

      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: name.trim(),
          avatar_url: avatarUrl,
          phone: cleanPhone,
          // @ts-ignore
          birth_date: dbDate,
          // @ts-ignore
          gender: gender
        })
        .eq("user_id", user?.id);

      if (error) throw error;

      toast.success("Perfil atualizado! Bem-vindo(a)!");
      await refreshProfile();
      setIsVisible(false);
    } catch (error: any) {
      toast.error("Erro ao atualizar perfil: " + error.message);
    } finally {
      setUpdating(false);
    }
  };

  if (authLoading) return null;
  if (!user) return <>{children}</>;
  if (!isVisible) return <>{children}</>;

  return (
    <div className="fixed inset-0 z-[100] bg-foreground/10 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-card/50 border border-foreground/10 w-full max-w-md p-6 rounded-3xl shadow-2xl animate-in fade-in zoom-in duration-300 my-8">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-primary/30">
            <User className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-2xl font-bold font-display text-gradient mb-2">Complete seu Perfil</h2>
          <p className="text-sm text-foreground/80 mb-3 leading-relaxed">
            Falta pouco! Complete os dados abaixo para acessar a plataforma.
          </p>
        </div>

        <form onSubmit={handleUpdateProfile} className="space-y-4">
          <div className="flex flex-col items-center gap-2 mb-4">
            <div className="relative group cursor-pointer">
              <div className="w-24 h-24 rounded-full border-2 border-dashed border-foreground/10 group-hover:border-primary/50 overflow-hidden flex items-center justify-center bg-foreground/10 transition-all">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-8 h-8 text-muted-foreground group-hover:text-primary transition-colors" />
                )}
                {uploading && (
                  <div className="absolute inset-0 bg-foreground/10 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-foreground" />
                  </div>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </div>
            <div className="text-center">
              <p className="text-[11px] text-primary uppercase tracking-widest font-black leading-tight">
                Foto de Rosto (Opcional)
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="name">Nome Completo</Label>
            <Input
              id="name"
              placeholder="Ex: Hudson Raone"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-foreground/10 border-foreground/10 focus:border-primary h-11"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="phone">WhatsApp</Label>
            <Input
              id="phone"
              placeholder="(11) 99999-9999"
              value={phone}
              onChange={handlePhoneChange}
              className="bg-foreground/10 border-foreground/10 focus:border-primary h-11"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="birthDate">Data Nasc.</Label>
              <Input
                id="birthDate"
                placeholder="DD/MM/AAAA"
                maxLength={10}
                value={birthDate}
                onChange={(e) => setBirthDate(formatToBRLDate(e.target.value))}
                className="bg-foreground/10 border-foreground/10 focus:border-primary h-11"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gender">Sexo</Label>
              <select
                id="gender"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="flex h-11 w-full rounded-md border border-foreground/10 bg-foreground/10 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-foreground"
                required
              >
                <option value="" disabled>Selecione</option>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
                <option value="O">Outro</option>
              </select>
            </div>
          </div>

          <Button
            type="submit"
            disabled={updating || uploading}
            className="w-full h-12 mt-2 bg-gradient-primary text-primary-foreground font-bold text-lg rounded-xl shadow-lg shadow-orange-500/20"
          >
            {updating ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : (
              "Salvar e Continuar"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ProfileCompletionGuard;
